import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const AccountingFirm = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my accounting firm business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps accounting firm businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for accounting firm businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most accounting firm businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my accounting firm business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like accounting firm. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Accounting Firm - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Accounting Firm CRM", url: "/industries/accounting-firm" }
  ];

  const industryData = {
    name: 'Accounting Firm',
    icon: '📊',
    title: 'Automate Client Communication & Fill Your Seasonal Workload',
    subtitle: 'Streamline tax season chaos, nurture year-round clients, and reduce admin time with automation',
    
    challenges: [
      'Overwhelmed during tax season with client communication and document collection',
      'Difficulty maintaining client relationships outside of tax season',
      'Spending hours chasing clients for missing documents and information',
      'Manual appointment scheduling consuming valuable billable time',
      'One-time tax clients not returning for ongoing bookkeeping or advisory services',
      'Hard to compete with larger firms for small business clients',
      'Compliance deadlines causing last-minute client panic and stress',
      'Not enough client reviews to build credibility for new prospects'
    ],
    
    solutions: [
      'Automated document collection sequences with deadline reminders reduce back-and-forth',
      'Year-round client nurture campaigns with tax tips and financial advice keep you top-of-mind',
      'Online appointment booking handles consultations without phone tag',
      'Automated tax deadline reminders sent 60, 30, and 7 days in advance reduce panic',
      'Service upgrade campaigns promote bookkeeping and advisory services to tax-only clients',
      'Client portal provides secure document sharing and real-time communication',
      'Review automation after tax filing builds credibility and attracts new clients',
      'Automated invoicing and payment tracking improves cash flow'
    ],
    
    features: [
      'Online Consultation Booking System',
      'Automated Document Collection Sequences',
      'Tax Deadline Reminder Campaigns (60/30/7 days)',
      'Secure Client Portal for Document Sharing',
      'Year-Round Tax Tip Email Sequences',
      'Service Upgrade Campaigns (Bookkeeping/Advisory)',
      'Automated Appointment Reminders',
      'Google Review Request After Filing',
      'Client Onboarding Automation',
      'Compliance Deadline Tracking',
      'Automated Invoicing & Payment Reminders',
      'Referral Request Automation'
    ],
    
    results: [
      'Reduce document collection time by 60%',
      '40% of tax clients convert to year-round services',
      'Save 15 hours/week during tax season',
      'Increase client retention by 55%',
      'Average 4.9★ Google rating',
      'Fill appointment calendar 3 weeks ahead'
    ],
    
    reviews: [
      {
        author: 'David Chen',
        location: 'London',
        text: 'The automated document collection has saved me countless hours during tax season. Clients get reminders with direct upload links and I no longer chase missing documents. What used to take 50 emails now happens automatically. Tax season stress is down 70%.',
        result: 'Saves 20 hours weekly during tax season'
      },
      {
        author: 'Sarah Mitchell',
        location: 'Manchester',
        text: 'The service upgrade campaigns have transformed my business model. 40% of my tax-only clients have signed up for monthly bookkeeping or quarterly advisory services. That\'s consistent revenue year-round instead of just seasonal work.',
        result: '40% tax clients now year-round clients'
      },
      {
        author: 'James Wilson',
        location: 'Birmingham',
        text: 'The tax deadline reminders have reduced last-minute panic dramatically. Clients appreciate the proactive communication and I\'m not dealing with stressed-out calls 2 days before deadlines. My client satisfaction scores have gone from 7.5 to 9.2 out of 10.',
        result: 'Client satisfaction increased to 9.2/10'
      },
      {
        author: 'Emma Roberts',
        location: 'Leeds',
        text: 'Online booking has been brilliant for consultations. Prospects can book discovery calls at 11pm or during lunch breaks. I\'ve converted 68% of online bookings into clients compared to 40% from phone enquiries because they\'re more serious.',
        result: '68% consultation-to-client conversion'
      },
      {
        author: 'Michael Brown',
        location: 'Bristol',
        text: 'The review automation took us from 8 Google reviews to 127 in 18 months. We\'re now the top-rated accounting firm in Bristol. New client enquiries have doubled and they specifically mention our reviews and communication.',
        result: '127 Google reviews, enquiries doubled'
      },
      {
        author: 'Rachel Taylor',
        location: 'Edinburgh',
        text: 'The client portal has professionalized my solo practice. Clients can upload documents, see their tax status, and message securely 24/7. I\'ve landed 3 corporate clients who said the portal made me look like a big firm.',
        result: '3 corporate clients signed'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Accounting Firm UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for accounting firm businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for accounting firm UK, accounting firm business software, accounting firm customer management, accounting firm lead generation UK"
        url="/industries/accounting-firm"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default AccountingFirm;